import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { createImperativePromise } from 'awesome-imperative-promise';

import { Box, Button, Card, Center, Grid, Group, Stack, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconChevronDown, IconChevronUp, IconFileOff, IconSettings, IconX } from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import AiDocumentEditor from '../components/AiDocumentEditor/AiDocumentEditor';
import AiChat from '../components/Chat/App/AiChat';
import ErrorMessage from '../components/ErrorMessage';
import GeneratedContentForm from '../components/GeneratedContent/GeneratedContentForm';
import GenerativePromptEditor from '../components/GeneratedContent/GenerativePromptEditor';
import ConfirmationModal from '../components/Modals/ConfirmationModal';
import Spinner from '../components/Spinner';
import {
  generatedContentSideIcons,
  marketingChannelsOptions,
  typeOptionsByChannel,
} from '../constants/GeneratedContent';
import useFetch from '../helpers/useFetch';
import { setDomain } from '../store/domainSlice';
import { models, providers } from './models';

const updateDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, id, payload) => {
  return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/generatedContent/${id}`, {
    method: 'PUT',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}, 300);

const checkProviderToken = (domain, provider) => {
  if (provider === 'chatGPT' && !domain.openAIToken) {
    return false;
  }

  if (provider === 'claude' && !domain.anthropicToken) {
    return false;
  }

  return true;
};

let promiseCancel;

const GeneratedContentDetails = ({ refreshContents, createButton }) => {
  const customFetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { domain } = useSelector((state) => state.domain);

  const { accountId, domainId, generatedContentId } = useParams();
  const [generatedContent, setGeneratedContent] = useState();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isLoading, setIsLoading] = useState();
  const [isLocked, setIsLocked] = useState();
  const [error, setError] = useState();
  const [errorGenerateContent, setErrorGenerateContent] = useState();
  const [userError, setUserError] = useState();
  const quillEditorRef = useRef();
  const lastSavedContent = useRef();
  const [cardBodyOpen, setCardBodyOpen] = useState(false);
  const [showAbortButton, setShowAbortButton] = useState(false);
  const controllerRef = useRef(null);
  const [brandContext, setBrandContext] = useState();
  const [generationType, setGenerationType] = useState('text');

  const fetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}/generatedContent`;
  const brandContextStartingUrl = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}`;
  const isFolder = generatedContent?.marketingChannel === 'folder';

  useEffect(() => {
    setBrandContext(domain.brandContext);
  }, [domain.brandContext]);

  useEffect(() => {
    setConfirmDeleteOpen(false);
  }, [location]);

  useEffect(() => {
    if (!generatedContentId) {
      setGeneratedContent(null);
      return;
    }
    customFetch(`${fetchUrl}/${generatedContentId}`)
      .then(async ({ generatedContent }) => {
        setGeneratedContent(generatedContent);
        const data = generatedContent?.data;
        if (data && typeof data === 'string') {
          const parsedData = JSON.parse(data);
          const dataBool = parsedData?.responseList?.length === 0;
          setCardBodyOpen(dataBool);
        } else {
          setCardBodyOpen(true);
        }
        lastSavedContent.current = data;
      })
      .catch(setError);
  }, [fetchUrl, customFetch, generatedContentId]);

  const scrollToBottom = () => {
    const editor = document.getElementsByClassName('ql-editor')?.[0];
    if (editor) {
      editor.scrollTop = editor.scrollHeight;
    }
  };

  const saveGeneratedContent = (editor) => {
    const currentContents = editor.getContents();
    const serializedContent = JSON.stringify(currentContents);

    setGeneratedContent((prev) => ({
      ...prev,
      data: serializedContent,
    }));

    updateGeneratedContent(serializedContent);
  };

  const insertTextIntoEditor = (text) => {
    const editor = quillEditorRef.current.getEditor();
    const length = editor.getLength();
    const newIndex = length - 1;

    editor.setSelection(newIndex);
    editor.insertText(newIndex, text, 'user');

    scrollToBottom();
    saveGeneratedContent(editor);
  };

  const editorFormatHtmlTags = () => {
    const editor = quillEditorRef.current.getEditor();
    const contents = editor.getText();
    const delta = editor.clipboard.convert(contents);
    editor.setContents(delta, 'silent');
    saveGeneratedContent(editor);
  };

  const generateContent = async (model) => {
    setErrorGenerateContent(false);
    setIsLoading(true);
    setIsLocked(true);

    try {
      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      const url = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/generatedContent/generate`;

      setShowAbortButton(true);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({ generatedContent, model }),
        signal,
      });

      if (response.ok) {
        setIsLoading(false);
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let result;

        while (!result?.done) {
          result = await reader.read();
          const data = decoder.decode(result.value);
          insertTextIntoEditor(data);
        }
      } else {
        setErrorGenerateContent({ message: `Error: ${response.statusText}` });
      }
    } catch (error) {
      // Aborting the stream emits AbortError we do not display it
      if (error.name !== 'AbortError') {
        setErrorGenerateContent(error);
      }
    } finally {
      editorFormatHtmlTags();
      setShowAbortButton(false);
      setIsLoading(false);
      setIsLocked(false);
    }
  };

  const abortStream = () => {
    controllerRef.current.abort('User Aborted Streaming');
    editorFormatHtmlTags();
    setShowAbortButton(false);
    setIsLocked(false);
  };

  const updateGeneratedContent = async (newContent) => {
    try {
      await customFetch(`/api/admin/accounts/${accountId}/domains/${domainId}/generatedContent/${generatedContentId}`, {
        method: 'PUT',
        body: JSON.stringify({ data: newContent }),
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange = (event, isInputContext) => {
    if (promiseCancel) {
      promiseCancel();
    }
    let updatedContent;

    if (isInputContext) {
      // this handles removing sections from GenerativePromptEditor
      const eventValue = Object.values(event)[0];
      if (eventValue == null) {
        const { ...inputContextWithoutExcludedKey } = generatedContent.inputContext;
        updatedContent = JSON.parse(
          JSON.stringify({
            ...generatedContent,
            inputContext: {
              ...inputContextWithoutExcludedKey,
            },
          })
        );
      } else {
        // this handles updates in input context data from GenerativePromptEditor
        updatedContent = JSON.parse(
          JSON.stringify({
            ...generatedContent,
            inputContext: {
              ...generatedContent.inputContext,
              ...event,
            },
          })
        );
      }
    } else if (event.target.data) {
      const updatedData = JSON.parse(event.target.data);
      const currentData = generatedContent.data ? JSON.parse(generatedContent.data) : {};
      updatedContent = {
        ...generatedContent,
        data: JSON.stringify({
          ...currentData,
          ...updatedData,
        }),
      };
    } else if (event.target.name === 'provider') {
      const { value } = event.target;
      const defaultModel = models[value][0].value;
      updatedContent = JSON.parse(JSON.stringify({ ...generatedContent, provider: value, model: defaultModel }));
    } else {
      const { name, value } = event.target;

      updatedContent = JSON.parse(JSON.stringify({ ...generatedContent, [name]: value }));
    }
    setGeneratedContent(updatedContent);
    const { promise, cancel } = createImperativePromise(
      updateDebounced(fetch, accountId, domainId, generatedContent.id, updatedContent)
    );
    promiseCancel = cancel;
    promise.then(refreshContents);
  };

  const handleBrandContextChange = async (e) => {
    const updatedBrandContext = {
      ...brandContext,
      [e.target.name]: {
        value: e.target.value,
      },
    };

    try {
      const response = await fetch(`${brandContextStartingUrl}/brandContext`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedBrandContext),
      });

      const result = await response.json();
      setBrandContext(result.brandContext);
      dispatch(setDomain({ ...domain, brandContext: result.brandContext }));
    } catch (error) {
      setError(error);
    }
  };

  const deleteGeneratedContent = (cb) => {
    fetch(`${fetchUrl}/${generatedContentId}`, {
      method: 'DELETE',
    })
      .then(() => {
        if (typeof cb === 'function') {
          cb();
        }
        navigate(location.pathname.replace(`/generatedContent/${generatedContent.id}`, '/generatedContent'));
      })
      .catch(setUserError);
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (generatedContent === undefined) {
    return <Spinner />;
  }

  if (generatedContent === null) {
    return (
      <Center style={{ flex: 1, width: '100%' }}>
        <Stack align="center" gap="xs">
          <IconFileOff size={24} />
          <Title c="dimmed" order={3} ta="center">
            Please choose a document to edit
          </Title>
          <Title c="dimmed" order={3}>
            or
          </Title>
          <Box mt="sm">{createButton}</Box>
        </Stack>
      </Center>
    );
  }

  const backButton = (
    <Button variant="default" onClick={() => navigate(-1)}>
      <IconArrowLeft /> Back
    </Button>
  );

  const deleteButton = (
    <Button color="red" onClick={() => setConfirmDeleteOpen(true)}>
      <IconX size={16} /> Delete
    </Button>
  );

  const marketingChannel = isFolder
    ? generatedContent.name
    : marketingChannelsOptions.find((option) => option.value === generatedContent.marketingChannel)?.label;

  let type;
  if (generatedContent.marketingChannel === 'blogPost') {
    type = generatedContent.type;
  } else {
    type = typeOptionsByChannel[generatedContent.marketingChannel].find((option) => {
      return option.value === generatedContent.type;
    })?.label;
  }

  return (
    <Grid>
      <ConfirmationModal
        show={confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={(cb) => deleteGeneratedContent(cb)}
        message={
          isFolder ? (
            <>
              All files inside this folder will be deleted! <br /> This action cannot be undone!
            </>
          ) : (
            'This action cannot be undone!'
          )
        }
      />

      <Grid.Col span={12} id="context-configuration">
        <Card withBorder mb="xs">
          <Card.Section
            inheritPadding
            py="sm"
            onClick={() => setCardBodyOpen(!cardBodyOpen)}
            style={{ cursor: 'pointer' }}
          >
            <Group justify="space-between">
              <Group gap="xs">
                {React.createElement(generatedContentSideIcons[generatedContent.marketingChannel], {
                  size: 20,
                  style: { color: 'var(--mantine-primary-color-filled)' },
                })}
                <Title order={5} mb={0}>
                  {marketingChannel}
                  {type && ` - ${type}`}
                </Title>
              </Group>
              {cardBodyOpen ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
            </Group>
          </Card.Section>
          {cardBodyOpen && (
            <>
              <GeneratedContentForm generatedContent={generatedContent} handleChange={handleChange} />

              {!['folder', 'chat', 'document'].includes(generatedContent.marketingChannel) && (
                <Card.Section inheritPadding py="md">
                  <GenerativePromptEditor
                    key={generatedContent.id}
                    brandContext={brandContext}
                    generatedContent={generatedContent}
                    generateContent={() => generateContent(generatedContent.model)}
                    setCardBodyOpen={setCardBodyOpen}
                    handleContextChange={handleChange}
                  />
                </Card.Section>
              )}
            </>
          )}
        </Card>
      </Grid.Col>
      {checkProviderToken(domain, generatedContent.provider) ? (
        <Grid.Col span={12}>
          {errorGenerateContent && (
            <Box ta="left">
              <ErrorMessage message={errorGenerateContent.message} />
            </Box>
          )}

          {generatedContent.marketingChannel === 'chat' ? (
            <AiChat
              generatedContent={generatedContent}
              setData={(value) => handleChange({ target: { name: 'data', value } })}
              brandContext={brandContext}
              cardBodyOpen={cardBodyOpen}
              setCardBodyOpen={setCardBodyOpen}
              handleBrandContextChange={handleBrandContextChange}
              generationType={generationType}
              setGenerationType={setGenerationType}
            />
          ) : !isFolder ? (
            <AiDocumentEditor
              key={generatedContentId}
              generatedContentId={generatedContentId}
              content={generatedContent.data || {}}
              quillEditorRef={quillEditorRef}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              isLocked={isLocked}
              aiModel={generatedContent.model}
              provider={generatedContent.provider}
              abortStream={abortStream}
              abortButtonVisible={showAbortButton}
              language={'en'}
              userError={userError}
              setUserError={setUserError}
              controllerRef={controllerRef}
              setShowAbortButton={setShowAbortButton}
              handleBrandContextChange={handleBrandContextChange}
            />
          ) : null}

          {userError && (
            <Box ta="right">
              <ErrorMessage message={userError.message} />
            </Box>
          )}

          <Group justify="flex-end" gap="xs" py="md" mt="md">
            {backButton}
            {deleteButton}
          </Group>
        </Grid.Col>
      ) : (
        <Grid.Col span={12}>
          <Card withBorder>
            <Title order={3}>
              {providers.find((e) => e.value === generatedContent.provider).label} API token not configured. Please go
              to settings add your token to continue.
            </Title>
            <Group justify="center">
              <Button
                component="a"
                href={`#/accounts/${domain.accountId}/domains/${domain.id}/settings/domain?tab=aiHubTokens`}
                size="xs"
                mr="xs"
                mb="xs"
              >
                <Text component="span" visibleFrom="md">
                  Update tokens{' '}
                </Text>
                <IconSettings size={16} />
              </Button>
            </Group>
          </Card>
        </Grid.Col>
      )}
    </Grid>
  );
};

export default GeneratedContentDetails;
